/**
 * Turning an autofill result into changes to the form — and knowing when the
 * operator should be asked first.
 *
 * The server already normalised every value to what the form accepts; this
 * module decides WHICH of them to write. The one rule that matters: a field
 * the operator has typed into is theirs. A value the message would put there
 * is a conflict to show, not a change to make silently.
 *
 * Cities and warehouses never appear here: the operator picks those by hand.
 *
 * Self-contained on purpose. The form route imports this file, so importing
 * anything back from the route would be a module cycle — and a top-level
 * `{ ...FIELD_LABEL }` here would throw a ReferenceError the moment the route
 * had not finished initialising. The labels are restated instead.
 */

import type { AutofillFields, WorkHours } from '@acs/shared'
import { WORK_HOURS } from '@acs/shared'

export type AutofillKey = keyof AutofillFields

/** Form order, which is also the order the fields light up in after a fill. */
export const AUTOFILL_ORDER: readonly AutofillKey[] = [
  'name',
  'phone',
  'email',
  'pin',
  'imapAppPassword',
  'workHours',
  'shiftPriority',
  'sinNumber',
  'dateOfBirth',
  'gender',
  'addressLine1',
  'addressLine2',
  'addressCity',
  'addressProvince',
  'addressPostalCode',
]

/** The labels the banner and the conflict list use. Match the form's own. */
export const AUTOFILL_LABEL: Readonly<Record<AutofillKey, string>> = Object.freeze({
  name: 'Name',
  phone: 'Phone',
  email: 'Email',
  pin: 'PIN',
  imapAppPassword: 'Gmail app password',
  workHours: 'Work hours',
  shiftPriority: 'Shift priority',
  sinNumber: 'SIN',
  dateOfBirth: 'Date of birth',
  gender: 'Gender',
  addressLine1: 'Address',
  addressLine2: 'Address line 2',
  addressCity: 'City',
  addressProvince: 'Province',
  addressPostalCode: 'Postal code',
})

/**
 * What the form holds for the fields a fill can touch. The form's own draft
 * satisfies it structurally — every one of these is a string on the draft
 * except the work hours, which are a list.
 */
export type AutofillValues = {
  readonly [K in AutofillKey]: K extends 'workHours' ? readonly string[] : string
}

/**
 * Exactly what to write, keyed by form field. A key is present only with a
 * value — never `undefined`, which a spread would write over the draft.
 */
export type AutofillPatch = {
  [K in AutofillKey]?: K extends 'workHours' ? WorkHours[] : string
}

export type ApplyMode = 'replace' | 'fill-empty'

export interface Conflict {
  field: AutofillKey
  current: string
  next: string
}

export interface AutofillPlan {
  patch: AutofillPatch
  /** The fields written, in form order — what lights up. */
  fields: AutofillKey[]
}

/* ------------------------------------------------------------------ *
 * Equality
 * ------------------------------------------------------------------ */

/**
 * Same value for the purpose of "would this change anything": whitespace and
 * case never count for the fields where the form does not care either, and
 * lists compare as sets.
 */
function canon(field: AutofillKey, value: unknown): string {
  if (Array.isArray(value)) return [...value].map(String).sort().join(' ')
  const s = typeof value === 'string' ? value.trim() : String(value ?? '')
  switch (field) {
    case 'email':
    case 'addressPostalCode':
      return s.toLowerCase().replace(/\s+/g, '')
    case 'phone':
    case 'pin':
    case 'sinNumber':
      return s.replace(/\D/g, '')
    case 'imapAppPassword':
      return s.toLowerCase().replace(/\s+/g, '')
    default:
      return s.replace(/\s+/g, ' ')
  }
}

function same(field: AutofillKey, a: unknown, b: unknown): boolean {
  return canon(field, a) === canon(field, b)
}

/**
 * True while the field holds nothing the operator typed. Tested on the RAW
 * value, not the canonical one: a phone box holding letters is not blank, it
 * is a mistake the operator made and may be about to fix.
 */
export function isBlank(value: unknown): boolean {
  if (Array.isArray(value)) return value.length === 0
  return typeof value !== 'string' || value.trim() === ''
}

/* ------------------------------------------------------------------ *
 * Display
 * ------------------------------------------------------------------ */

/** A value as the operator reads it on the form, for the conflict list. */
export function displayValue(field: AutofillKey, value: unknown): string {
  if (value === undefined || value === null || value === '') return ''
  if (Array.isArray(value)) return value.join(', ')
  if (field === 'dateOfBirth') {
    // dd/mm/yyyy, exactly as the form's own three boxes show it.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value))
    return m ? `${m[3]}/${m[2]}/${m[1]}` : String(value)
  }
  return String(value)
}

/* ------------------------------------------------------------------ *
 * The decision
 * ------------------------------------------------------------------ */

/** The message's value for a field, or `undefined` when it had none. */
function offered(fields: AutofillFields, field: AutofillKey): string | WorkHours[] | undefined {
  const value = fields[field]
  if (value === undefined) return undefined
  if (Array.isArray(value)) return value.length === 0 ? undefined : value
  return value === '' ? undefined : value
}

/** Fields where the message disagrees with something the operator already typed. */
export function findConflicts(current: AutofillValues, fields: AutofillFields): Conflict[] {
  const out: Conflict[] = []
  for (const field of AUTOFILL_ORDER) {
    const next = offered(fields, field)
    if (next === undefined) continue
    const mine = current[field]
    if (isBlank(mine)) continue
    if (same(field, mine, next)) continue
    out.push({ field, current: displayValue(field, mine), next: displayValue(field, next) })
  }
  return out
}

/**
 * What to write. `replace` takes every value the message had; `fill-empty`
 * leaves the operator's own entries alone. Either way a value the form
 * already holds is not rewritten, so it does not light up as a change.
 */
export function planAutofill(
  current: AutofillValues,
  fields: AutofillFields,
  mode: ApplyMode
): AutofillPlan {
  const patch: AutofillPatch = {}
  const written: AutofillKey[] = []

  for (const field of AUTOFILL_ORDER) {
    if (mode === 'fill-empty' && !isBlank(current[field])) continue

    if (field === 'workHours') {
      const hours = fields.workHours
      if (hours === undefined || hours.length === 0) continue
      // Canonical order, as the form's own toggles would have left it.
      const next = WORK_HOURS.filter((h) => hours.includes(h))
      if (next.length === 0 || same(field, current.workHours, next)) continue
      patch.workHours = next
    } else {
      const value = fields[field]
      if (value === undefined || value === '') continue
      if (same(field, current[field], value)) continue
      patch[field] = value
    }
    written.push(field)
  }

  return { patch, fields: written }
}
